export type ProcessLogKind = 'analysis' | 'context' | 'thinking' | 'search' | 'action' | 'result' | 'error';
export type ProcessLogStatus = 'active' | 'completed' | 'error';

export interface ProcessLogEntry {
  id: string;
  kind: ProcessLogKind;
  status: ProcessLogStatus;
  title: string;
  detail?: string;
  timestamp?: number;
}

export interface AttachmentItem {
  file?: File;
  name: string;
  type: string;
  size: number;
  thumbnailUrl?: string;
}

export interface CollabStatusEntry {
  phase: string;
  icon: string;
  label: string;
  detail?: string;
}

export interface MessageMetadata {
  pendingTaskId?: string;
  pendingNodeId?: string;
  taskStatus?: string;
  [key: string]: unknown;
}

// 🔵 #26: Message 保留单一 interface（Discriminated Union 在大型代码库中改动过大）
// 但通过注释明确 type 与 *Data 字段的绑定关系，代码中应使用 `m.type === 'search' && m.searchData` 做类型守卫
export interface Message {
  id?: string;
  role: 'user' | 'model';
  text: string;
  introText?: string;
  type?: 'text' | 'search' | 'workflow' | 'image' | 'video' | 'generation_confirm' | 'error';
  /** 仅 type==='search' 时应有值 */
  searchData?: any;
  /** 仅 type==='workflow' 时应有值 */
  workflowData?: any;
  /** 仅 type==='image'/'video' 时应有值 */
  mediaData?: any;
  attachments?: AttachmentItem[];
  references?: { id: string; thumbnailUrl?: string; title?: string }[];
  processLogs?: ProcessLogEntry[];
  /** 仅 type==='generation_confirm' 时应有值 */
  generationOptions?: any;
  /** 仅 type==='generation_confirm' 时应有值 */
  pendingAttachments?: any[];
  /** 仅 type==='generation_confirm' 时应有值 */
  pendingReferences?: any[];
  /** 仅 type==='generation_confirm' 时应有值 */
  pendingUserText?: string;
  /** 仅 type==='generation_confirm' 时应有值 */
  pendingRefNodeIds?: string[];
  metadata?: MessageMetadata;
  isHistory?: boolean;
}

export interface ChatHistoryEntry {
  id: string;
  topic: string;
  date: string;
  updatedAt: string;  // ISO 时间戳，用于排序
  messages: Message[];
}

export const FEATURE_GRIDS: Record<string, { rows: number; cols: number }> = {
  '多机位九宫格': { rows: 3, cols: 3 },
  '剧情推演四宫格': { rows: 2, cols: 2 },
  '25宫格连贯分镜': { rows: 5, cols: 5 },
  '电影级光影校正': { rows: 1, cols: 1 },
  '角色三视图生成': { rows: 1, cols: 3 },
  '画面推演-3秒后': { rows: 1, cols: 1 },
  '画面推演-5秒前': { rows: 1, cols: 1 },
};

export const PROCESS_KIND_LABELS: Record<ProcessLogKind, string> = {
  analysis: '分析',
  context: '上下文',
  thinking: '思考',
  search: '搜索',
  action: '执行',
  result: '结果',
  error: '错误',
};

export interface BackgroundTask {
  taskId: string;
  taskName: string;
  status: 'searching' | 'running' | 'completed' | 'failed';
  estimatedTime?: number;
  agentId?: string;
}

export interface ToolCallSegment {
  type: 'tool_call';
  toolName: string;
  icon?: string;
  content: string;
  position: number;
}

export interface AgentCallSegment {
  type: 'agent_call';
  targetId: string;
  task: string;
  context?: string;
  position: number;
}

export interface RouteSegment {
  type: 'route';
  agentId: string;
  position: number;
}

export interface ReminderSegment {
  type: 'reminder';
  content: string;
  position: number;
}

export interface ClarificationRequest {
  callId: string;
  question: string;
  options: string[];
  timestamp: string;
}

export interface CollaborationStatus {
  callId: string;
  status?: 'pending' | 'running' | 'completed' | 'error' | 'failed' | 'cancelled';
  progress?: number;
  message?: string;
  resultSummary?: string;
  cost?: number;
  errorCode?: string;
  errorMessage?: string;
  dispatcherDegraded?: boolean;
  degradationLevel?: number;
  degradationMessage?: string;
}
